import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle, Send, Lock, Mail, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { track } from "../lib/track";
import { waNumber, generateWhatsAppMessage } from "../lib/whatsapp";
import logoWhite from "../assets/logo-mark-white.png";

const G = "#C88A24", GB = "#E5A83A", INK = "#0B0806", IVORY = "#F4ECD9", MUTE = "#A7997E";
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function BhajanThankYou() {
    const [cfg, setCfg] = useState(null);
    const [order, setOrder] = useState(null);
    const [ans, setAns] = useState({});
    const [busy, setBusy] = useState(false);
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const orderId = useMemo(() => params.get("order") || "", [params]);
    // Secret order token from the confirmation link (signed-in buyers can open it without one).
    const orderToken = useMemo(() => params.get("t") || "", [params]);
    const preview = useMemo(() => params.get("preview") === "1", [params]);

    useEffect(() => {
        document.title = "Thank you — MUVIARTS Bhajan";
        Promise.all([api.get("/bhajan"), orderId ? api.get(`/bhajan/order/${orderId}`, { params: orderToken ? { t: orderToken } : {} }) : Promise.resolve({ data: null })])
            .then(([c, o]) => {
                setCfg(c.data);
                if (preview && !o.data) {
                    o = { data: { paid: true, amount: c.data?.booking_amount || 999, reference: "PREVIEW-1234", offer_name: c.data?.offer_name || "Bhajan Production", name: "", email: "you@example.com" } };
                }
                setOrder(o.data);
                if (o.data) setAns((p) => ({ ...p, name: o.data.name || "" }));
                if (!preview && o.data && o.data.paid) {
                    track("Purchase", { value: o.data.amount, currency: "INR", content_name: o.data.offer_name, content_type: "product", content_ids: ["bhajan-booking"], num_items: 1 }, { eventID: o.data.order_id });
                    track("BookingSuccess", { value: o.data.amount, currency: "INR" });
                }
            })
            .catch(() => {});
    }, [orderId]);

    const fields = cfg?.thankyou_form || [];
    const setF = (id, v) => setAns((p) => ({ ...p, [id]: v }));

    const buildMessage = () => {
        const ref = order?.reference || "";
        let msg = `Hi MuviArts, I've paid ${inr(order?.amount)} to book the ${order?.offer_name || "Bhajan Production offer"}. My booking is confirmed (Ref: ${ref}). Here are my project details:\n`;
        fields.forEach((f) => {
            const v = ans[f.id];
            if (v && String(v).trim()) msg += `\n• ${f.label}: ${v}`;
        });
        return msg;
    };

    const submit = async () => {
        if (preview) { toast.info("Preview mode — WhatsApp handoff is disabled here."); return; }
        const missing = fields.filter((f) => f.required && !String(ans[f.id] || "").trim());
        if (missing.length) { toast.error(`Please fill: ${missing.map((m) => m.label).join(", ")}`); return; }
        // Open the tab synchronously (inside the click gesture) so the browser
        // doesn't block the popup after the async save completes.
        const waWin = window.open("", "_blank");
        setBusy(true);
        try {
            if (orderId) await api.post(`/bhajan/order/${orderId}/details`, { answers: ans, t: orderToken });
            track("LeadFormSubmit", { content_name: "bhajan_project_details" });
        } catch { toast.error("Couldn't save your details, but we'll still open WhatsApp…"); }
        const url = `https://wa.me/${waNumber(cfg)}?text=${encodeURIComponent(buildMessage())}`;
        if (waWin && !waWin.closed) waWin.location.href = url; else window.location.href = url;
        toast.success("Opening WhatsApp with your details…");
        setBusy(false);
    };

    const chatDirect = () => {
        if (preview) { toast.info("Preview mode — WhatsApp handoff is disabled here."); return; }
        track("WhatsAppClick", { state: "PAID_BOOKING" });
        const url = `https://wa.me/${waNumber(cfg)}?text=${encodeURIComponent(generateWhatsAppMessage({ state: "PAID_BOOKING", cfg, ref: order?.reference || "" }))}`;
        const w = window.open(url, "_blank");
        if (!w) window.location.href = url;
    };

    if (!cfg) return <div className="min-h-screen grid place-items-center" style={{ background: INK, color: GB }}><div className="animate-pulse tracking-[0.3em] text-sm">MUVIARTS</div></div>;

    const inputCls = "mt-1.5 w-full rounded-xl px-4 py-3 text-sm outline-none";
    const inputStyle = { background: "#160f08", border: `1px solid ${G}44`, color: IVORY };

    return (
        <div style={{ background: INK, color: IVORY, fontFamily: "Manrope, sans-serif" }} className="min-h-screen relative">
            {preview && (
                <div className="relative z-20 text-center py-2 text-xs font-semibold tracking-wide" style={{ background: GB, color: INK }} data-testid="ty-preview-banner">
                    PREVIEW MODE — showing saved settings with a sample receipt. Not a real booking.
                </div>
            )}
            <div className="pointer-events-none fixed inset-0" style={{ background: `radial-gradient(1000px 500px at 70% -10%, ${G}22, transparent 60%)` }} />
            <nav className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center">
                <a href="/bhajans" data-testid="ty-logo"><img src={logoWhite} alt="MUVIARTS" className="h-9 w-auto object-contain" /></a>
            </nav>

            <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pb-20">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center pt-6">
                    <div className="mx-auto h-16 w-16 rounded-full grid place-items-center" style={{ background: `linear-gradient(120deg,${G},${GB})` }}><CheckCircle2 className="h-9 w-9" style={{ color: INK }} /></div>
                    <h1 className="mt-5 font-semibold leading-tight" style={{ fontFamily: "'Cormorant Garamond',serif", color: IVORY, fontSize: "clamp(1.9rem,5vw,3rem)" }} data-testid="ty-heading">{cfg.thankyou_heading}</h1>
                    <p className="mt-3 text-base" style={{ color: MUTE }}>{cfg.thankyou_subheading}</p>
                    {order && order.paid && (
                        <div className="mt-5 inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-1 rounded-2xl border px-5 py-3 text-sm" style={{ borderColor: `${G}44`, background: "#100d09" }} data-testid="ty-receipt">
                            <span style={{ color: MUTE }}>Paid <b style={{ color: IVORY }}>{inr(order.amount)}</b></span>
                            <span style={{ color: MUTE }}>Ref <b style={{ color: IVORY }}>{order.reference}</b></span>
                            <span style={{ color: MUTE }}>{order.offer_name}</span>
                        </div>
                    )}
                    {order && !order.paid && <p className="mt-4 text-sm" style={{ color: "#e07a5f" }}>We couldn't confirm this booking as paid. If you were charged, please contact us on WhatsApp.</p>}
                </motion.div>

                {cfg.thankyou_enabled !== false && (
                    <div className="mt-8 rounded-3xl border p-5 sm:p-7 space-y-4" style={{ borderColor: `${G}33`, background: "#0E0B08" }} data-testid="ty-form">
                        {fields.map((f) => (
                            <div key={f.id}>
                                <label className="text-sm font-semibold" style={{ color: IVORY }}>{f.label}{f.required && <span style={{ color: GB }}> *</span>}</label>
                                {f.type === "textarea" && <textarea rows={4} value={ans[f.id] || ""} onChange={(e) => setF(f.id, e.target.value)} placeholder={f.placeholder || ""} data-testid={`ty-field-${f.id}`} className={inputCls} style={inputStyle} />}
                                {f.type === "text" && <input value={ans[f.id] || ""} onChange={(e) => setF(f.id, e.target.value)} placeholder={f.placeholder || ""} data-testid={`ty-field-${f.id}`} className={inputCls} style={inputStyle} />}
                                {f.type === "select" && (
                                    <select value={ans[f.id] || ""} onChange={(e) => setF(f.id, e.target.value)} data-testid={`ty-field-${f.id}`} className={inputCls} style={inputStyle}>
                                        <option value="">Select…</option>
                                        {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                )}
                                {f.type === "radio" && (
                                    <div className="mt-2 space-y-2">
                                        {(f.options || []).map((o) => (
                                            <button key={o} type="button" onClick={() => setF(f.id, o)} data-testid={`ty-field-${f.id}-${o.slice(0, 8)}`} className="w-full text-left rounded-xl px-4 py-2.5 text-sm transition-colors" style={ans[f.id] === o ? { border: `1px solid ${GB}`, background: "#1c140b", color: IVORY } : { border: `1px solid ${G}33`, color: MUTE }}>{o}</button>
                                        ))}
                                    </div>
                                )}
                                {f.type === "checkbox" && (
                                    <div className="mt-2 space-y-2">
                                        {(f.options || []).map((o) => {
                                            const arr = Array.isArray(ans[f.id]) ? ans[f.id] : [];
                                            const on = arr.includes(o);
                                            return <button key={o} type="button" onClick={() => setF(f.id, on ? arr.filter((x) => x !== o) : [...arr, o])} data-testid={`ty-field-${f.id}-${o.slice(0, 8)}`} className="w-full text-left rounded-xl px-4 py-2.5 text-sm" style={on ? { border: `1px solid ${GB}`, background: "#1c140b", color: IVORY } : { border: `1px solid ${G}33`, color: MUTE }}>{on ? "✓ " : ""}{o}</button>;
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <button onClick={submit} disabled={busy} data-testid="ty-submit" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-bold disabled:opacity-60" style={{ background: `linear-gradient(120deg,${G},${GB})`, color: INK }}>
                                <Send className="h-4 w-4" /> {busy ? "Sending…" : "Submit & continue on WhatsApp"}
                            </button>
                            <button onClick={chatDirect} data-testid="ty-chat-direct" className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold border" style={{ borderColor: `${G}55`, color: GB }}>
                                <MessageCircle className="h-4 w-4" /> Chat directly instead
                            </button>
                        </div>
                        <p className="text-center text-[11px] flex items-center justify-center gap-1.5" style={{ color: MUTE }}><Lock className="h-3 w-3" /> Your details go straight to our production team on WhatsApp.</p>
                    </div>
                )}

                <div className="mt-6 rounded-2xl border p-4 text-sm flex flex-col sm:flex-row items-start sm:items-center gap-3" style={{ borderColor: `${G}22`, background: "#100d09" }} data-testid="ty-access-note">
                    <div className="flex items-center gap-2" style={{ color: MUTE }}><Mail className="h-4 w-4" style={{ color: G }} /> We've emailed your receipt + this page link{order?.email ? <> to <b style={{ color: IVORY }}>{order.email}</b></> : ""}.</div>
                    <a href="/dashboard" className="sm:ml-auto inline-flex items-center gap-1.5 font-semibold" style={{ color: GB }}><LayoutDashboard className="h-4 w-4" /> Reopen from your dashboard</a>
                </div>
            </div>
        </div>
    );
}
